import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";
import { Users, Car, CheckCircle2, DollarSign, Ticket, Search, QrCode, Receipt, Loader2, CheckCheck, Bell, Send, RefreshCw } from "lucide-react";

const TABS = [
  { k: "verify", t: "Verify & Issue", i: Search },
  { k: "vehicles", t: "Vehicles", i: Car },
  { k: "members", t: "Members", i: Users },
  { k: "tickets", t: "Wash Tickets", i: Ticket },
  { k: "transactions", t: "Payments", i: Receipt },
  { k: "notifications", t: "Reminders", i: Bell },
  { k: "qr", t: "QR Code", i: QrCode },
];

function Stat({ icon: I, label, value }) {
  return (
    <div className="border border-white/10 bg-surface p-6">
      <I className="w-5 h-5 text-volt mb-4" />
      <div className="font-head font-black text-4xl">{value}</div>
      <div className="overline text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    expired: "text-amber-400 border-amber-500/40 bg-amber-500/10",
    inactive: "text-muted-foreground border-white/15",
  };
  return <span className={`overline border px-2 py-1 ${map[status] || map.inactive}`}>{status}</span>;
}

export default function Admin() {
  const [tab, setTab] = useState("verify");
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [members, setMembers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [txns, setTxns] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [testPlate, setTestPlate] = useState("");
  const [billingBusy, setBillingBusy] = useState(false);
  const [cfg, setCfg] = useState(null);

  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    loadStats();
    api.get("/config", { params: { origin: window.location.origin } }).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "vehicles") api.get("/admin/vehicles").then((r) => setVehicles(r.data));
    if (tab === "members") api.get("/admin/members").then((r) => setMembers(r.data));
    if (tab === "tickets") api.get("/admin/tickets").then((r) => setTickets(r.data));
    if (tab === "transactions") api.get("/admin/transactions").then((r) => setTxns(r.data));
    if (tab === "notifications") api.get("/admin/notifications").then((r) => setNotifs(r.data));
  }, [tab]);

  const loadNotifs = () => api.get("/admin/notifications").then((r) => setNotifs(r.data));

  const runBilling = async () => {
    setBillingBusy(true);
    try {
      const res = await api.post("/admin/run-billing");
      toast.success(`Billing run: ${res.data.reminders_sent} reminders, ${res.data.renewals_processed} renewals`);
      loadStats(); loadNotifs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBillingBusy(false); }
  };

  const sendTestReminder = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/test-reminder", { plate: testPlate });
      const n = res.data.notification;
      if (n.status === "sent") toast.success(`Reminder SMS sent to ${n.to}`);
      else toast.error(`SMS ${n.status}: ${n.error || ""}`);
      loadNotifs();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setSearching(true); setResult(null);
    try {
      const res = await api.get(`/admin/verify`, { params: { plate: query } });
      setResult(res.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const issue = async (plate) => {
    try {
      const res = await api.post("/admin/wash/issue", { plate });
      toast.success(`Ticket ${res.data.code} issued for ${plate}`);
      loadStats();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const redeem = async (id) => {
    try { await api.post(`/admin/tickets/${id}/redeem`); toast.success("Ticket redeemed"); api.get("/admin/tickets").then((r) => setTickets(r.data)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="overline text-volt mb-2">Staff Console</div>
            <h1 className="font-head font-extrabold text-4xl uppercase tracking-tight">Admin Dashboard</h1>
          </div>
          <button data-testid="run-billing-btn" onClick={runBilling} disabled={billingBusy}
            className="flex items-center gap-2 border border-white/15 px-5 py-3 overline hover:border-volt hover:text-white transition-colors disabled:opacity-60">
            {billingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-volt" />} Run Billing Now
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-10">
          <Stat icon={Users} label="Members" value={stats?.total_members ?? "—"} />
          <Stat icon={Car} label="Cars" value={stats?.total_vehicles ?? "—"} />
          <Stat icon={CheckCircle2} label="Active Subs" value={stats?.active_subscriptions ?? "—"} />
          <Stat icon={Ticket} label="Washes Today" value={stats?.tickets_today ?? "—"} />
          <Stat icon={DollarSign} label={`Revenue ${stats?.currency || "AUD"}`} value={stats ? `$${stats.total_revenue}` : "—"} />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-px bg-white/10 border border-white/10 mb-8">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-5 py-3 overline transition-colors ${tab === t.k ? "bg-volt text-white" : "bg-background text-muted-foreground hover:text-white"}`}>
              <t.i className="w-4 h-4" /> {t.t}
            </button>
          ))}
        </div>

        {/* Verify & Issue */}
        {tab === "verify" && (
          <div className="max-w-2xl">
            <form onSubmit={verify} className="flex gap-3 mb-6">
              <input data-testid="verify-plate-input" required placeholder="ENTER NUMBER PLATE" value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())}
                className="flex-1 bg-input border border-white/10 px-4 py-3 plate uppercase text-white focus:outline-none focus:ring-2 focus:ring-volt" />
              <button data-testid="verify-submit-btn" disabled={searching} className="bg-volt text-white px-6 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-60">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
              </button>
            </form>

            {result && !result.found && (
              <div data-testid="verify-not-found" className="border border-red-500/30 bg-red-500/10 p-6 text-red-400">
                No vehicle found with plate <span className="plate font-bold">{result.plate}</span>.
              </div>
            )}
            {result && result.found && (
              <div data-testid="verify-result" className="border border-white/10 bg-surface p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="inline-flex items-center bg-white text-black plate font-bold text-2xl px-3 py-1 border-2 border-black mb-3">{result.plate}</div>
                    <div className="text-sm text-muted-foreground">{result.owner_name} · {result.owner_email}</div>
                    <div className="mt-3"><StatusPill status={result.status} /></div>
                    {result.current_period_end && <div className="overline text-muted-foreground mt-2">Valid until {new Date(result.current_period_end).toLocaleDateString("en-AU")}</div>}
                  </div>
                  <button data-testid="issue-ticket-btn" disabled={!result.active} onClick={() => issue(result.plate)}
                    className="flex items-center gap-2 bg-volt text-white px-6 py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Ticket className="w-4 h-4" /> Issue Wash Ticket
                  </button>
                </div>
                {!result.active && <p className="text-amber-400 text-sm mt-4">Membership inactive — cannot issue a ticket.</p>}
              </div>
            )}
          </div>
        )}

        {/* Vehicles */}
        {tab === "vehicles" && (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-vehicles-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-surface text-left overline text-muted-foreground">
                <th className="p-4">Plate</th><th className="p-4">Owner</th><th className="p-4">Status</th><th className="p-4">Valid Until</th>
              </tr></thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-t border-white/10">
                    <td className="p-4 plate font-bold">{v.plate}</td>
                    <td className="p-4">{v.owner_name}<div className="text-muted-foreground text-xs">{v.owner_email}</div></td>
                    <td className="p-4"><StatusPill status={v.status} /></td>
                    <td className="p-4 text-muted-foreground">{v.current_period_end ? new Date(v.current_period_end).toLocaleDateString("en-AU") : "—"}</td>
                  </tr>
                ))}
                {vehicles.length === 0 && <tr><td className="p-6 text-muted-foreground" colSpan={4}>No vehicles.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Members */}
        {tab === "members" && (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-members-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-surface text-left overline text-muted-foreground">
                <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Cars</th><th className="p-4">Active</th>
              </tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-white/10">
                    <td className="p-4">{m.name}</td>
                    <td className="p-4 text-muted-foreground">{m.email}</td>
                    <td className="p-4 text-muted-foreground">{m.phone || "—"}</td>
                    <td className="p-4">{m.vehicle_count}</td>
                    <td className="p-4 text-emerald-400">{m.active_count}</td>
                  </tr>
                ))}
                {members.length === 0 && <tr><td className="p-6 text-muted-foreground" colSpan={5}>No members.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Tickets */}
        {tab === "tickets" && (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-tickets-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-surface text-left overline text-muted-foreground">
                <th className="p-4">Code</th><th className="p-4">Plate</th><th className="p-4">Source</th><th className="p-4">Issued</th><th className="p-4">Status</th><th className="p-4"></th>
              </tr></thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} className="border-t border-white/10">
                    <td className="p-4 font-head font-bold text-lg">{t.code}</td>
                    <td className="p-4 plate font-bold">{t.plate}</td>
                    <td className="p-4 text-muted-foreground overline">{t.source}</td>
                    <td className="p-4 text-muted-foreground">{new Date(t.issued_at).toLocaleString("en-AU")}</td>
                    <td className="p-4"><StatusPill status={t.status === "redeemed" ? "inactive" : "active"} /></td>
                    <td className="p-4">{t.status === "issued" && (
                      <button data-testid={`redeem-${t.code}`} onClick={() => redeem(t.id)} className="flex items-center gap-1 overline text-volt hover:text-white transition-colors"><CheckCheck className="w-4 h-4" /> Redeem</button>
                    )}</td>
                  </tr>
                ))}
                {tickets.length === 0 && <tr><td className="p-6 text-muted-foreground" colSpan={6}>No tickets yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Transactions */}
        {tab === "transactions" && (
          <div className="border border-white/10 overflow-x-auto" data-testid="admin-transactions-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-surface text-left overline text-muted-foreground">
                <th className="p-4">Plate</th><th className="p-4">Email</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Date</th>
              </tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.session_id} className="border-t border-white/10">
                    <td className="p-4 plate font-bold">{t.plate}</td>
                    <td className="p-4 text-muted-foreground">{t.email}</td>
                    <td className="p-4">${t.amount} {(t.currency || "").toUpperCase()}</td>
                    <td className="p-4"><StatusPill status={t.payment_status === "paid" ? "active" : "inactive"} /></td>
                    <td className="p-4 text-muted-foreground">{new Date(t.created_at).toLocaleDateString("en-AU")}</td>
                  </tr>
                ))}
                {txns.length === 0 && <tr><td className="p-6 text-muted-foreground" colSpan={5}>No payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Notifications */}
        {tab === "notifications" && (
          <div data-testid="admin-notifications">
            <div className="border border-white/10 bg-surface p-5 mb-6">
              <div className="overline text-muted-foreground mb-2 flex items-center gap-2"><Send className="w-4 h-4 text-volt" /> Send Test Reminder (SMS + Email)</div>
              <p className="text-xs text-muted-foreground mb-4">Sends a renewal reminder immediately to the plate owner's phone via Twilio. Auto-reminders also fire {"~"}3 days before each renewal.</p>
              <form onSubmit={sendTestReminder} className="flex gap-3">
                <input data-testid="test-reminder-input" required placeholder="NUMBER PLATE" value={testPlate} onChange={(e) => setTestPlate(e.target.value.toUpperCase())}
                  className="flex-1 bg-input border border-white/10 px-4 py-3 plate uppercase text-white focus:outline-none focus:ring-2 focus:ring-volt" />
                <button data-testid="test-reminder-btn" className="bg-volt text-white px-6 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors flex items-center gap-2">
                  <Send className="w-4 h-4" /> Send
                </button>
              </form>
            </div>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-surface text-left overline text-muted-foreground">
                  <th className="p-4">Channel</th><th className="p-4">To</th><th className="p-4">Message</th><th className="p-4">Status</th><th className="p-4">Sent</th>
                </tr></thead>
                <tbody>
                  {notifs.map((n) => (
                    <tr key={n.id} className="border-t border-white/10">
                      <td className="p-4 overline text-volt">{n.channel}</td>
                      <td className="p-4 text-muted-foreground">{n.to}</td>
                      <td className="p-4 max-w-md text-muted-foreground">{n.body}</td>
                      <td className="p-4"><StatusPill status={n.status === "sent" || n.status === "logged" ? "active" : (n.status === "failed" ? "expired" : "inactive")} /></td>
                      <td className="p-4 text-muted-foreground">{new Date(n.created_at).toLocaleString("en-AU")}</td>
                    </tr>
                  ))}
                  {notifs.length === 0 && <tr><td className="p-6 text-muted-foreground" colSpan={5}>No reminders sent yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* QR */}
        {tab === "qr" && cfg && (
          <div data-testid="admin-qr" className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="overline text-volt mb-2">Join / Sign Up</div>
              <p className="text-muted-foreground text-sm mb-6 tracking-wide">Put this on flyers, windows and socials. Customers scan to open the site and become a member.</p>
              <div className="border border-white/10 bg-white p-6 inline-block">
                <img src={cfg.join_qr_base64} alt="Join QR code" className="w-64 h-64" />
              </div>
              <p className="overline text-muted-foreground mt-4 break-all">{cfg.join_url}</p>
            </div>
            <div>
              <div className="overline text-volt mb-2">Wash Check-In</div>
              <p className="text-muted-foreground text-sm mb-6 tracking-wide">Display this at {cfg.address}. Members scan on arrival to confirm their plate and get a wash ticket.</p>
              <div className="border border-white/10 bg-white p-6 inline-block">
                <img src={cfg.qr_base64} alt="Wash check-in QR code" className="w-64 h-64" />
              </div>
              <p className="overline text-muted-foreground mt-4 break-all">{cfg.wash_url}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
